import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
}: ModalProps) {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/60 backdrop-blur-sm duration-300 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <DialogPanel
            transition
            className={cn(
              'w-full transform overflow-hidden rounded-[20px] bg-dark-card border border-border-subtle p-6 text-left align-middle shadow-modal',
              'duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
              {
                'max-w-sm': size === 'sm',
                'max-w-md': size === 'md',
                'max-w-lg': size === 'lg',
                'max-w-4xl': size === 'xl',
                'max-w-7xl': size === 'full',
              }
            )}
          >
            {(title || showCloseButton) && (
              <div className="flex items-start justify-between mb-4">
                {title && (
                  <DialogTitle as="h3" className="text-lg font-medium leading-6 text-text-primary font-heading">
                    {title}
                  </DialogTitle>
                )}
                {showCloseButton && (
                  <button
                    type="button"
                    className="text-text-muted hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent/50 rounded-md transition-colors"
                    onClick={onClose}
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
            {description && (
              <p className="text-sm text-text-secondary mb-4">
                {description}
              </p>
            )}
            <div>{children}</div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default Modal;
